import { useMemo, useState } from "react";
import { Invoice } from "src/types";
import { Panel, SectionHeader } from "./DashboardPrimitives";
import { useTranslation } from "react-i18next";

// ─── Types ────────────────────────────────────────────────────────────────────

type Period = "month" | "year" | "3m" | "6m" | "12m" | "all";
type SeriesKey = "sent" | "m1" | "m2" | "m3" | "legal";

interface Bucket {
    label: string;
    sent: number;
    m1: number;
    m2: number;
    m3: number;
    legal: number;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const SVG_W = 600;
const SVG_H = 200;
const PAD = { top: 16, right: 12, bottom: 34, left: 56 };
const PLOT_W = SVG_W - PAD.left - PAD.right;
const PLOT_H = SVG_H - PAD.top - PAD.bottom;

const PERIODS: { key: Period; tKey: string }[] = [
    { key: "month", tKey: "dashboard.timeline.thisMonth" },
    { key: "year",  tKey: "dashboard.timeline.thisYear" },
    { key: "3m",    tKey: "dashboard.timeline.last3m" },
    { key: "6m",    tKey: "dashboard.timeline.last6m" },
    { key: "12m",   tKey: "dashboard.timeline.last12m" },
    { key: "all",   tKey: "dashboard.timeline.allTime" },
];

const SERIES: { key: SeriesKey; color: string; fillColor: string }[] = [
    { key: "sent",  color: "#6366f1", fillColor: "rgba(99,102,241,0.10)"  },
    { key: "m1",    color: "#22c55e", fillColor: "rgba(34,197,94,0.10)"   },
    { key: "m2",    color: "#f97316", fillColor: "rgba(249,115,22,0.10)"  },
    { key: "m3",    color: "#ef4444", fillColor: "rgba(239,68,68,0.10)"   },
    { key: "legal", color: "#6b7280", fillColor: "rgba(107,114,128,0.08)" },
];

// ─── Bucket generation ────────────────────────────────────────────────────────

function categorize(inv: Invoice): SeriesKey {
    const d = inv.dunning ?? 0;
    if (d >= 4) return "legal";
    if (d === 3) return "m3";
    if (d === 2) return "m2";
    if (d === 1) return "m1";
    return "sent";
}

function generateBuckets(invoices: Invoice[], period: Period): Bucket[] {
    const now = new Date();
    const emptyBucket = (): Omit<Bucket, "label"> => ({ sent: 0, m1: 0, m2: 0, m3: 0, legal: 0 });

    if (period === "month") {
        const year  = now.getFullYear();
        const month = now.getMonth();
        const days  = new Date(year, month + 1, 0).getDate();
        const buckets: Bucket[] = Array.from({ length: days }, (_, i) => ({
            label: String(i + 1),
            ...emptyBucket(),
        }));
        for (const inv of invoices) {
            if (!inv.sentDate) continue;
            const d = new Date(inv.sentDate);
            if (d.getFullYear() !== year || d.getMonth() !== month) continue;
            const idx = d.getDate() - 1;
            const remaining = Math.max(0, (inv.amount ?? 0) - (inv.paidAmount ?? 0));
            buckets[idx][categorize(inv)] += remaining;
        }
        return buckets;
    }

    let startDate: Date;
    const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    let endDate = currentMonthStart;

    if (period === "year") {
        startDate = new Date(now.getFullYear(), 0, 1);
    } else if (period === "3m") {
        startDate = new Date(now.getFullYear(), now.getMonth() - 2, 1);
    } else if (period === "6m") {
        startDate = new Date(now.getFullYear(), now.getMonth() - 5, 1);
    } else if (period === "12m") {
        startDate = new Date(now.getFullYear(), now.getMonth() - 11, 1);
    } else {
        // "all" — no invoices: fall back to current year so the chart stays visible
        if (!invoices.length) {
            startDate = new Date(now.getFullYear(), 0, 1);
        } else {
            let earliest = new Date();
            let latest   = new Date(0);
            for (const inv of invoices) {
                if (!inv.sentDate) continue;
                const d = new Date(inv.sentDate);
                if (!isNaN(d.getTime())) {
                    if (d < earliest) earliest = d;
                    if (d > latest)   latest   = d;
                }
            }
            startDate = new Date(earliest.getFullYear(), earliest.getMonth(), 1);
            // Cap at current month so we don't show trailing empty or future months
            const latestMonthStart = new Date(latest.getFullYear(), latest.getMonth(), 1);
            endDate = latestMonthStart <= currentMonthStart ? latestMonthStart : currentMonthStart;
        }
    }

    const months: { year: number; month: number }[] = [];
    let cur = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
    while (cur <= endDate) {
        months.push({ year: cur.getFullYear(), month: cur.getMonth() });
        cur = new Date(cur.getFullYear(), cur.getMonth() + 1, 1);
    }
    if (!months.length) return [];

    const showYear = months.length > 14;
    const buckets: Bucket[] = months.map(({ year, month }) => ({
        label: new Date(year, month, 1).toLocaleString("de-DE", {
            month: "short",
            ...(showYear ? { year: "2-digit" } : {}),
        }),
        ...emptyBucket(),
    }));

    for (const inv of invoices) {
        if (!inv.sentDate) continue;
        const d = new Date(inv.sentDate);
        const idx = months.findIndex(m => m.year === d.getFullYear() && m.month === d.getMonth());
        if (idx < 0) continue;

        const remaining = Math.max(0, (inv.amount ?? 0) - (inv.paidAmount ?? 0));
        buckets[idx][categorize(inv)] += remaining;
    }

    return buckets;
}

// ─── SVG path helpers ─────────────────────────────────────────────────────────

function buildLinePath(points: [number, number][]): string {
    if (!points.length) return "";
    let d = `M ${points[0][0].toFixed(1)} ${points[0][1].toFixed(1)}`;
    for (let i = 1; i < points.length; i++) {
        const [x0, y0] = points[i - 1];
        const [x1, y1] = points[i];
        const cx = ((x0 + x1) / 2).toFixed(1);
        d += ` C ${cx} ${y0.toFixed(1)}, ${cx} ${y1.toFixed(1)}, ${x1.toFixed(1)} ${y1.toFixed(1)}`;
    }
    return d;
}

function buildAreaPath(points: [number, number][], baseY: number): string {
    if (!points.length) return "";
    const line = buildLinePath(points);
    const last  = points[points.length - 1];
    const first = points[0];
    return `${line} L ${last[0].toFixed(1)} ${baseY} L ${first[0].toFixed(1)} ${baseY} Z`;
}

function niceMax(rawMax: number): number {
    if (rawMax <= 0) return 100;
    const mag = Math.pow(10, Math.floor(Math.log10(rawMax)));
    const candidates = [1, 2, 2.5, 5, 10].map(f => f * mag);
    return candidates.find(c => c >= rawMax) ?? rawMax;
}

function fmtYAxis(n: number): string {
    if (n === 0) return "0 €";
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1)}M €`;
    if (n >= 1_000)     return `${(n / 1_000).toFixed(n % 1_000 === 0 ? 0 : 1)}k €`;
    return `${Math.round(n)} €`;
}

// ─── SVG chart ────────────────────────────────────────────────────────────────

function TimelineSvg({
    buckets,
    activeSeries,
}: {
    buckets: Bucket[];
    activeSeries: Set<SeriesKey>;
}) {
    const n = buckets.length;
    if (n === 0) return <p className="timeline-empty">—</p>;

    const maxRaw = Math.max(
        ...buckets.flatMap(b =>
            SERIES.filter(s => activeSeries.has(s.key)).map(s => b[s.key]),
        ),
        1,
    );
    const yMax   = niceMax(maxRaw);
    const GRIDS  = 4;
    const gridVs = Array.from({ length: GRIDS + 1 }, (_, i) => (yMax * i) / GRIDS);
    const baseY  = PAD.top + PLOT_H;

    const xOf = (i: number) =>
        n === 1 ? PAD.left + PLOT_W / 2 : PAD.left + (i / (n - 1)) * PLOT_W;
    const yOf = (v: number) => PAD.top + (1 - v / yMax) * PLOT_H;

    const xStep = Math.max(1, Math.ceil(n / 10));

    return (
        <svg
            viewBox={`0 0 ${SVG_W} ${SVG_H}`}
            className="timeline-svg"
        >
            {/* Horizontal grid lines */}
            {gridVs.map((v, i) => {
                const y = yOf(v);
                return (
                    <g key={i}>
                        <line
                            x1={PAD.left} y1={y}
                            x2={PAD.left + PLOT_W} y2={y}
                            stroke="#f0f0f4" strokeWidth={1}
                        />
                        <text
                            x={PAD.left - 6} y={y}
                            textAnchor="end" dominantBaseline="middle"
                            fontSize={9} fill="#9ca3af"
                        >
                            {fmtYAxis(v)}
                        </text>
                    </g>
                );
            })}

            {/* X axis baseline */}
            <line
                x1={PAD.left} y1={baseY}
                x2={PAD.left + PLOT_W} y2={baseY}
                stroke="#e5e7eb" strokeWidth={1}
            />

            {/* X axis labels */}
            {buckets.map((b, i) => {
                const show = i % xStep === 0 || i === n - 1;
                if (!show) return null;
                return (
                    <text
                        key={i}
                        x={xOf(i)} y={baseY + 12}
                        textAnchor="middle" fontSize={9} fill="#9ca3af"
                    >
                        {b.label}
                    </text>
                );
            })}

            {/* Area fills (below lines) */}
            {SERIES.filter(s => activeSeries.has(s.key)).map(({ key, fillColor }) => {
                const pts: [number, number][] = buckets.map((b, i) => [xOf(i), yOf(b[key])]);
                return (
                    <path key={`area-${key}`} d={buildAreaPath(pts, baseY)} fill={fillColor} />
                );
            })}

            {/* Lines + dots */}
            {SERIES.filter(s => activeSeries.has(s.key)).map(({ key, color }) => {
                const pts: [number, number][] = buckets.map((b, i) => [xOf(i), yOf(b[key])]);
                return (
                    <g key={`line-${key}`}>
                        <path d={buildLinePath(pts)} fill="none" stroke={color} strokeWidth={1.5} />
                        {pts.map(([x, y], i) => (
                            <circle key={i} cx={x} cy={y} r={2.5} fill={color} />
                        ))}
                    </g>
                );
            })}
        </svg>
    );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function InvoiceTimelineChart({ invoices }: { invoices: Invoice[] }) {
    const { t } = useTranslation();
    const [period, setPeriod]           = useState<Period>("year");
    const [activeSeries, setActiveSeries] = useState<Set<SeriesKey>>(
        new Set<SeriesKey>(["sent", "m1", "m2", "m3", "legal"]),
    );

    const buckets = useMemo(() => generateBuckets(invoices, period), [invoices, period]);

    const toggleSeries = (key: SeriesKey) => {
        setActiveSeries(prev => {
            const next = new Set(prev);
            if (next.has(key) && next.size > 1) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    return (
        <Panel>
            <div className="timeline-header">
                <SectionHeader title={t("dashboard.timeline.title")} />
                <div className="timeline-series">
                    {SERIES.map(({ key }) => (
                        <button
                            key={key}
                            className={`timeline-series-btn timeline-series-btn--${key}${activeSeries.has(key) ? " timeline-series-btn--on" : ""}`}
                            onClick={() => toggleSeries(key)}
                        >
                            <span className="timeline-series-dot" />
                            {t(`dashboard.timeline.${key}`)}
                        </button>
                    ))}
                </div>
            </div>

            <div className="timeline-periods">
                {PERIODS.map(({ key, tKey }) => (
                    <button
                        key={key}
                        className={`timeline-chip${period === key ? " timeline-chip--active" : ""}`}
                        onClick={() => setPeriod(key)}
                    >
                        {t(tKey)}
                    </button>
                ))}
            </div>

            <TimelineSvg buckets={buckets} activeSeries={activeSeries} />
        </Panel>
    );
}
